import React, {useEffect, useRef, useState} from "react";
import styled from "styled-components";
import CircularProgress from "@material-ui/core/CircularProgress";
import {
  LlmRunFilterKeys,
  LlmRunForComparison,
} from "common/flowmaster/types/llm_run";
import {Link} from "react-router";
import InputRows from "./input_rows";
import Selectors from "common_components/flowmaster/llm_run/selectors";
import {LlmRunQueryParams} from "routes/llm_run/utils/filtering/get_llm_run_query_params";
import Field from "common_components/flowmaster/llm_run/filtering/filter_field";
import InlineMultiSelect from "./inline_multi_select";
import DateFilter from "common_components/flowmaster/llm_run/date_filter";
import OutputRows from "./output_rows";
import SummaryRow from "./summary_rows";
import {OrganisationId} from "common/types/organisation";
import {
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
} from "@material-ui/core";
import LinkIcon from "material-ui/svg-icons/content/link";
import NumberTextField from "routes/llm_run/llm_run_list/components/numeric_text_field";
import {LlmRunComparisonSessionStorage} from "../util/session_storage";
import InputOutputSelect from "./input_output_select";
import GetAppIcon from "@material-ui/icons/GetApp";
import {LlmRunCompareDownloadDialog} from "./download_dialog";

const Loading = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.2);
`;

const Table = styled.table<{fitToPage: boolean}>`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;

  th:first-child,
  td:first-child {
    width: 10em;
    white-space: nowrap;
    padding-left: 1em;
    padding-right: 0.5em;
  }

  th:not(:first-child),
  td:not(:first-child) {
    width: ${({fitToPage}) => (fitToPage ? "auto" : "30em")};
    padding: 0.2em;
  }

  tbody > tr {
    border-bottom: 1px solid lightgray;
  }

  th {
    background-color: white;
    position: sticky;
    top: 0;
    z-index: 1;
    border-bottom: lightgray;
  }
`;

type Props = {
  isLoading: boolean;
  organisationId: OrganisationId;
  llmRuns: LlmRunForComparison[];
  inputNames: string[];
  values: LlmRunQueryParams;
  updateValue: (obj: Record<LlmRunFilterKeys, string | number>) => void;
  maxRuns: number;
  updateMaxRuns: (value: number) => void;
};

export default function llmRunCompareComponent({
  isLoading,
  organisationId,
  llmRuns,
  inputNames,
  values,
  updateValue,
  maxRuns,
  updateMaxRuns,
}: Props) {
  const summaryKeys = [
    "workflow_id",
    "project_id",
    "document_id",
    "issue_id",
    "clausepart_id",
    "parent_run_id",
    "batch_id",
    "username",
    "cost",
    "creation_date",
  ];

  useEffect(() => {
    setShownInputs(
      shownInputs.filter(shownInput => inputNames.includes(shownInput)),
    );
  }, [JSON.stringify(inputNames)]);

  const sessionObject = new LlmRunComparisonSessionStorage();
  const sessionHiddenInputs = sessionObject.getHiddenInputs();

  const [shownInputs, setShownInputs] = useState(
    inputNames.filter(input => !sessionHiddenInputs.includes(input)),
  );

  const handleShownInputsChange = (newShownInputs: string[]) => {
    sessionObject.setHiddenInputs(
      inputNames.filter(name => !newShownInputs.includes(name)),
    );
    setShownInputs(newShownInputs);
  };

  const outputs = Array.from(
    new Set(llmRuns.map(run => Object.keys(run.data.data_store.output)).flat()),
  ) as string[];

  const sessionHiddenOutputs = sessionObject.getHiddenOutputs();

  const [shownOutputs, setShownOutputs] = useState(
    outputs.filter(output => !sessionHiddenOutputs.includes(output)),
  );

  const handleShownOutputsChange = newShownOutputs => {
    sessionObject.setHiddenOutputs(
      outputs.filter(name => !newShownOutputs.includes(name)),
    );
    setShownOutputs(newShownOutputs);
  };

  const results = Array.from(
    new Set(
      llmRuns.map(run => Object.keys(run.data.data_store.results)).flat(),
    ),
  ) as string[];

  const sessionHiddenResults = sessionObject.getHiddenResults();

  const [shownResults, setShownResults] = useState(
    results.filter(result => !sessionHiddenResults.includes(result)),
  );

  const handleShownResultsChange = newShownResults => {
    sessionObject.setHiddenResults(
      results.filter(name => !newShownResults.includes(name)),
    );
    setShownResults(newShownResults);
  };

  const [showSummary, setShowSummary] = useState(true);

  const handleShowSummaryChange = () => {
    setShowSummary(!showSummary);
  };

  const [fitToPage, setFitToPage] = useState(true);

  const tableRef = useRef(null);
  return (
    <div
      style={{
        display: "flex",
        width: "100%",
        flexDirection: "column",
        overflowX: "auto",
      }}
    >
      {isLoading && (
        <Loading>
          <CircularProgress />
        </Loading>
      )}
      <Header
        inputs={inputNames}
        shownInputs={shownInputs}
        setShownInputs={handleShownInputsChange}
        outputs={outputs}
        shownOutputs={shownOutputs}
        setShownOutputs={handleShownOutputsChange}
        results={results}
        shownResults={shownResults}
        setShownResults={handleShownResultsChange}
        values={values}
        updateValue={updateValue}
        maxRuns={maxRuns}
        updateMaxRuns={updateMaxRuns}
        tableRef={tableRef}
        summaryKeys={summaryKeys}
        showSummary={showSummary}
        fitToPage={fitToPage}
        setFitToPage={setFitToPage}
      />
      <div>
        <LlmRunCompareTable
          {...{
            organisationId,
            llmRuns,
            inputs: inputNames,
            shownInputs,
            outputs,
            shownOutputs,
            results,
            shownResults,
            handleShownOutputsChange,
            handleShownInputsChange,
            handleShownResultsChange,
            tableRef,
            summaryKeys,
            showSummary,
            handleShowSummaryChange,
            fitToPage,
          }}
        />
      </div>
    </div>
  );
}

function Header({
  inputs,
  shownInputs,
  setShownInputs,
  outputs,
  shownOutputs,
  setShownOutputs,
  results,
  shownResults,
  setShownResults,
  values,
  updateValue,
  maxRuns,
  updateMaxRuns,
  tableRef,
  summaryKeys,
  showSummary,
  fitToPage,
  setFitToPage,
}) {
  const [open, setOpen] = useState(false);

  const showDownloadDialog = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div
      style={{
        backgroundColor: "#f6f6f6",
        padding: "0 1em",
        borderBottom: "1px solid #aaa",
        marginBottom: "1em",
        zIndex: 1,
        position: "sticky",
        left: 0,
      }}
    >
      <div style={{display: "flex", justifyContent: "space-between"}}>
        <div style={{display: "flex"}}>
          <h1>LLM Runs Compare</h1>
          <LlmRunCompareDownloadDialog
            summaryKeys={summaryKeys}
            inputKeys={inputs}
            outputKeys={outputs}
            resultsKeys={results}
            showSummary={showSummary}
            shownInputKeys={shownInputs}
            shownOutputKeys={shownOutputs}
            shownResultsKeys={shownResults}
            onClose={handleClose}
            open={open}
            tableRef={tableRef}
          />
        </div>
        <div style={{display: "flex"}}>
          <div>
            <FormControlLabel
              label={"Fit to page"}
              control={
                <Checkbox
                  checked={fitToPage}
                  onChange={() => setFitToPage(!fitToPage)}
                  color="primary"
                />
              }
            />
          </div>
          <div>
            <DateFilter values={values} updateValue={updateValue} />
            <div
              style={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "space-between",
              }}
            >
              <NumberTextField
                value={maxRuns}
                label="Max Results"
                step={100}
                onChange={value => updateMaxRuns(value)}
              />
              <Button
                onClick={showDownloadDialog}
                endIcon={<GetAppIcon />}
                variant="outlined"
              >
                Download
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div style={{display: "flex", justifyContent: "space-between"}}>
        <OtherSelectors
          inputs={inputs}
          shownInputs={shownInputs}
          setShownInputs={setShownInputs}
          outputs={outputs}
          shownOutputs={shownOutputs}
          setShownOutputs={setShownOutputs}
          results={results}
          shownResults={shownResults}
          setShownResults={setShownResults}
          values={values}
          updateValue={updateValue}
        />
        <Selectors values={values} updateValue={updateValue} />
      </div>
    </div>
  );
}

function OtherSelectors({
  inputs,
  shownInputs,
  setShownInputs,
  outputs,
  shownOutputs,
  setShownOutputs,
  results,
  shownResults,
  setShownResults,
  values,
  updateValue,
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "0.5em",
      }}
    >
      <InputOutputSelect
        label="inputs"
        fields={inputs}
        shownFieldNames={shownInputs}
        setShownFieldNames={setShownInputs}
      />
      <Field
        label="Input Contains"
        value={values.input_contains ?? ""}
        updator={(value: string) => updateValue({input_contains: value})}
      />
      {outputs.length > 0 && (
        <InputOutputSelect
          label="outputs"
          fields={outputs}
          shownFieldNames={shownOutputs}
          setShownFieldNames={setShownOutputs}
        />
      )}
      {results.length > 0 && (
        <InputOutputSelect
          label="results"
          fields={results}
          shownFieldNames={shownResults}
          setShownFieldNames={setShownResults}
        />
      )}
      <Field
        label="Cost"
        value={values.min_cost ?? ""}
        updator={(value: number) => updateValue({min_cost: value})}
      />
      <InlineMultiSelect
        label="Reason"
        value={values.reasons ?? []}
        menuItems={[
          {
            value: "classify_clausepart",
            label: "Classify Clausepart",
          },
          {value: "document_upload", label: "Upload"},
          {value: "remote_api", label: "Remote API"},
          {value: "reprocess_issue", label: "Reprocess"},
          {value: "child", label: "Child"},
        ]}
        updator={value => updateValue({reasons: value})}
      />
      <InlineMultiSelect
        label="Status"
        value={values.status ?? []}
        menuItems={[
          {
            value: "in_progress",
            label: "In Progress",
          },
          {value: "complete", label: "Complete"},
          {value: "failed", label: "Failed"},
        ]}
        updator={value => updateValue({status: value})}
      />
    </div>
  );
}

function LlmRunCompareTable({
  organisationId,
  llmRuns,
  inputs,
  shownInputs,
  outputs,
  shownOutputs,
  results,
  shownResults,
  handleShownOutputsChange,
  handleShownInputsChange,
  handleShownResultsChange,
  tableRef,
  summaryKeys,
  showSummary,
  handleShowSummaryChange,
  fitToPage,
}) {
  return (
    <Table ref={tableRef} fitToPage={fitToPage}>
      <thead style={{verticalAlign: "top"}}>
        <tr id={"header"}>
          <th></th>
          {llmRuns.map(({id, workflow_name}) => (
            <th key={id}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "0.5em",
                }}
              >
                {id}
                <Link
                  to={`/organisation/${organisationId}/llm_run/${id}/detail`}
                >
                  <IconButton size="small" style={{padding: 0}}>
                    <LinkIcon />
                  </IconButton>
                </Link>
              </div>
              <div style={{fontWeight: "normal", textAlign: "left"}}>
                {workflow_name}
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <SummaryRow
          organisationId={organisationId}
          llmRuns={llmRuns}
          summaryKeys={summaryKeys}
          showSummary={showSummary}
          handleShowSummaryChange={handleShowSummaryChange}
        />
        <InputRows
          llmRuns={llmRuns}
          inputNames={inputs}
          shownInputs={shownInputs}
          setShownInputs={handleShownInputsChange}
        />
        {outputs.length > 0 && (
          <OutputRows
            llmRuns={llmRuns}
            outputKeys={outputs}
            shownOutputs={shownOutputs}
            handleShownOutputsChange={handleShownOutputsChange}
          />
        )}
        {results.length > 0 && (
          <OutputRows
            llmRuns={llmRuns}
            outputKeys={results}
            shownOutputs={shownResults}
            handleShownOutputsChange={handleShownResultsChange}
            title={"Results"}
            idPrefix="results"
            dataKey="results"
          />
        )}
      </tbody>
    </Table>
  );
}
